import { z } from 'zod';
import { AppError, FormCheckboxField, FormInputField, FormSelectionCardGroupField } from '@mews/framework';
import { DateTimeField, EnumSelectField, NumberInputField, SelectField } from '../../fields';
import { Compound } from '../compound';
import { unwrap, Unwrap } from '../unwrap';
import { PredefinedField, simpleComponentMap } from './predefined';
import { CommonProps, FieldRenderer } from './types';

export type FieldProps<
    FieldSchema extends z.ZodTypeAny = z.ZodTypeAny,
    FieldNames extends Record<string, string> = Record<string, string>,
> = CommonProps &
    (
        | { render?: FieldRenderer<FieldSchema, FieldNames>; field?: never }
        | { field?: PredefinedField<FieldSchema>; render?: never }
    );

/**
 * Returns a field renderer for a given field definition. Extracts additional information from related zod schema.
 */
export const getFieldRenderer = <FieldSchema extends z.ZodTypeAny>(
    field: PredefinedField<FieldSchema> | undefined,
    schema: Unwrap<FieldSchema>
): FieldRenderer<FieldSchema> => {
    if (!field) {
        return getDefaultRenderer(schema);
    }
    switch (field.type) {
        case 'enum': {
            const { type, ...fieldProps } = field;
            const isArray = schema instanceof z.ZodArray;
            const internalSchema = isArray ? unwrap(schema._def.type) : schema;

            if (internalSchema instanceof z.ZodNativeEnum) {
                return (props) => (
                    <EnumSelectField
                        enumClass={internalSchema._def.values}
                        multiselect={isArray}
                        {...props}
                        {...fieldProps}
                    />
                );
            }
            if (internalSchema instanceof z.ZodUnion) {
                const enumSchema = internalSchema._def.options
                    .map(unwrap)
                    .find((option: z.ZodTypeAny) => option instanceof z.ZodNativeEnum);
                if (enumSchema) {
                    return (props) => (
                        <EnumSelectField
                            enumClass={enumSchema._def.values}
                            multiselect={isArray}
                            {...props}
                            {...fieldProps}
                        />
                    );
                }
            }
            throw new AppError('Invalid enum schema');
        }
        case 'select': {
            const { type, ...fieldProps } = field;
            return (props) => <SelectField multiselect={schema instanceof z.ZodArray} {...props} {...fieldProps} />;
        }
        case 'cardSelect': {
            const { type, ...fieldProps } = field;
            return ({ placeholder, ...props }) => (
                <FormSelectionCardGroupField
                    type={schema instanceof z.ZodArray ? 'checkbox' : 'radio'}
                    {...props}
                    {...fieldProps}
                />
            );
        }
        case 'dateTime': {
            const { type, ...fieldProps } = field;
            return (props) => <DateTimeField inputSpacing="200" {...props} {...fieldProps} />;
        }
        case 'checkbox': {
            const { type, ...fieldProps } = field;
            return ({ label, placeholder, ...props }) => (
                <FormCheckboxField {...props} {...fieldProps}>
                    {label}
                </FormCheckboxField>
            );
        }
        case 'number': {
            const { type, ...fieldProps } = field;
            return (props) => <NumberInputField type="number" {...props} {...fieldProps} />;
        }
        case 'compound': {
            const { type, ...fieldProps } = field;
            if (!(schema instanceof z.ZodObject)) {
                throw new AppError('Invalid compound schema');
            }
            return (props) => <Compound namePrefix={props.name} schema={schema as never} {...fieldProps} />;
        }
        case 'date':
        case 'file':
        case 'hidden':
        case 'switch':
        case 'text':
        case 'textarea':
        case 'counter':
        case 'checkboxGroup':
        case 'time': {
            const { type, ...fieldProps } = field;
            const Component = simpleComponentMap[field.type];
            // type safety is enforced in the form definition but here we have a union of all possible field props
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (props) => <Component {...props} {...(fieldProps as any)} />;
        }
    }
};

/**
 * Returns a field renderer based on a zod schema.
 */
const getDefaultRenderer = <FieldSchema extends z.ZodTypeAny>(
    schema: Unwrap<FieldSchema>
): FieldRenderer<FieldSchema> => {
    if (schema instanceof z.ZodRecord || schema instanceof z.ZodObject || schema instanceof z.ZodDiscriminatedUnion) {
        throw new AppError("Nested types don't have a default renderer, use a custom render function.");
    }
    if (schema instanceof z.ZodBoolean || (schema instanceof z.ZodLiteral && typeof schema._def.value === 'boolean')) {
        return ({ label, placeholder, ...props }) => <FormCheckboxField {...props}>{label}</FormCheckboxField>;
    }
    if (schema instanceof z.ZodNumber) {
        if (schema.isInt) {
            return (props) => <NumberInputField type="number" {...props} />;
        }
        return (props) => <FormInputField type="number" {...props} />;
    }
    if (
        schema instanceof z.ZodUnion &&
        schema._def.options.map(unwrap).some((option: z.ZodTypeAny) => option instanceof z.ZodNumber)
    ) {
        if (
            schema._def.options
                .map(unwrap)
                .some((option: z.ZodTypeAny) => option instanceof z.ZodNumber && option.isInt)
        ) {
            return (props) => <NumberInputField type="number" {...props} />;
        }
        return (props) => <FormInputField type="number" {...props} />;
    }
    return (props) => <FormInputField {...props} />;
};
